/** Create or edit a catalog item (single item, package, stock settings). */
import { DataTable } from "@primereact/ui/datatable";
import { type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { CatalogItemResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Dialog } from "@primereact/ui/dialog";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { Select } from "@primereact/ui/select";
import { Textarea } from "@primereact/ui/textarea";
import type { Dispatch, SetStateAction } from "react";
import type { Location } from "./stock-operations.js";
import type { ItemDraft, PackageItemDraft } from "./routes.js";
import { MutationError, Section } from "../shared.js";

export function ItemEditor({
  draft,
  setDraft,
  editorOpen,
  setEditorOpen,
  submit,
  saving,
  saveError,
  locations,
  itemOptions,
}: {
  draft: ItemDraft;
  setDraft: Dispatch<SetStateAction<ItemDraft>>;
  editorOpen: boolean;
  setEditorOpen: (open: boolean) => void;
  submit: (event: FormEvent) => void;
  saving: boolean;
  saveError: unknown;
  locations: Location[] | undefined;
  itemOptions: CatalogItemResponse[] | undefined;
}) {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={editorOpen}
      modal
      onOpenChange={(event: { value: boolean | undefined }) =>
        setEditorOpen(Boolean(event.value))
      }
      style={{ width: "min(60rem, calc(100vw - 2rem))" }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Popup>
            <Dialog.Header>
              <Dialog.Title>
                {draft.id
                  ? t("catalog.edit", { defaultValue: "Edit item" })
                  : t("catalog.add", { defaultValue: "Add item" })}
              </Dialog.Title>
              <Dialog.HeaderActions>
                <Dialog.Close className="pi pi-times" />
              </Dialog.HeaderActions>
            </Dialog.Header>
            <form onSubmit={submit}>
              <Dialog.Content className="space-y-4">
                <Field
                  label={t("catalog.name", { defaultValue: "Name" })}
                  required
                >
                  <Input
                    required
                    value={draft.name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setDraft({ ...draft, name: event.target.value })
                    }
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t("catalog.sku", { defaultValue: "SKU" })}>
                    <Input
                      value={draft.sku}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setDraft({ ...draft, sku: event.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label={t("catalog.barcode", { defaultValue: "Barcode" })}
                  >
                    <Input
                      value={draft.barcode}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setDraft({ ...draft, barcode: event.target.value })
                      }
                    />
                  </Field>
                  <Field label={t("catalog.kind", { defaultValue: "Kind" })}>
                    <Select.Root
                      value={draft.kind}
                      onValueChange={(event: { value: unknown }) => {
                        const kind = String(event.value) as ItemDraft["kind"];
                        setDraft({
                          ...draft,
                          kind,
                          stockEnabled:
                            kind === "item"
                              ? true
                              : draft.kind === "item"
                                ? false
                                : draft.stockEnabled,
                          packageItems:
                            kind === "item" ? [] : draft.packageItems,
                        });
                      }}
                      fluid
                      options={[
                        {
                          value: "item",
                          label: t("catalog.item", { defaultValue: "Item" }),
                        },
                        {
                          value: "package",
                          label: t("catalog.package", {
                            defaultValue: "Package",
                          }),
                        },
                      ]}
                      optionLabel="label"
                      optionValue="value"
                    >
                      <Select.Trigger type="button">
                        <Select.Value />
                        <Select.Indicator className="pi pi-chevron-down" />
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Positioner>
                          <Select.Popup>
                            <Select.List />
                          </Select.Popup>
                        </Select.Positioner>
                      </Select.Portal>
                    </Select.Root>
                  </Field>
                  <Field
                    label={t("catalog.price", { defaultValue: "Unit price" })}
                  >
                    <InputNumber.Root
                      fluid
                      min={0}
                      step={0.01}
                      value={
                        draft.unitPrice === "" ? null : Number(draft.unitPrice)
                      }
                      onValueChange={(event: { value: number | null }) =>
                        setDraft({
                          ...draft,
                          unitPrice:
                            event.value == null ? "" : String(event.value),
                        })
                      }
                    >
                      <InputNumber.Input as={Input} />
                    </InputNumber.Root>
                  </Field>
                  <Field label={t("catalog.tax", { defaultValue: "VAT %" })}>
                    <InputNumber.Root
                      fluid
                      min={0}
                      step={0.1}
                      value={
                        draft.taxRate === "" ? null : Number(draft.taxRate)
                      }
                      onValueChange={(event: { value: number | null }) =>
                        setDraft({
                          ...draft,
                          taxRate:
                            event.value == null ? "" : String(event.value),
                        })
                      }
                    >
                      <InputNumber.Input as={Input} />
                    </InputNumber.Root>
                  </Field>
                </div>
                {draft.kind === "package" ? (
                  <Section
                    title={t("catalog.packageItems", {
                      defaultValue: "Package items",
                    })}
                    actions={
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        disabled={
                          !itemOptions?.some(
                            (item) =>
                              !draft.packageItems.some(
                                (component) => component.itemId === item.id,
                              ),
                          )
                        }
                        onClick={() =>
                          setDraft({
                            ...draft,
                            packageItems: [
                              ...draft.packageItems,
                              { itemId: "", quantity: "1" },
                            ],
                          })
                        }
                      >
                        {t("catalog.addComponent", {
                          defaultValue: "Add item",
                        })}
                      </Button>
                    }
                  >
                    <DataTable.Root
                      data={draft.packageItems.map((component, index) => ({
                        ...component,
                        index,
                      }))}
                      dataKey="index"
                    >
                      <DataTable.TableContainer>
                        <DataTable.Table>
                          <DataTable.THead>
                            <DataTable.Row>
                              <DataTable.THeadCell>
                                {t("catalog.item", { defaultValue: "Item" })}
                              </DataTable.THeadCell>
                              <DataTable.THeadCell>
                                {t("commercial.quantity", {
                                  defaultValue: "Quantity",
                                })}
                              </DataTable.THeadCell>
                              <DataTable.THeadCell
                                aria-label={t("common.actions")}
                              />
                            </DataTable.Row>
                          </DataTable.THead>
                          <DataTable.TBody>
                            {(context: { item: unknown }) => {
                              const component =
                                context.item as PackageItemDraft & {
                                  index: number;
                                };
                              return (
                                <DataTable.Row key={component.index}>
                                  <DataTable.Cell>
                                    <Select.Root
                                      required
                                      fluid
                                      aria-label={t("catalog.item", {
                                        defaultValue: "Package item",
                                      })}
                                      value={component.itemId || null}
                                      onValueChange={(event: {
                                        value: unknown;
                                      }) =>
                                        setDraft({
                                          ...draft,
                                          packageItems: draft.packageItems.map(
                                            (row, index) =>
                                              index === component.index
                                                ? {
                                                    ...row,
                                                    itemId: String(
                                                      event.value ?? "",
                                                    ),
                                                  }
                                                : row,
                                          ),
                                        })
                                      }
                                      options={(itemOptions ?? [])
                                        .filter(
                                          (item) =>
                                            item.id === component.itemId ||
                                            !draft.packageItems.some(
                                              (row) => row.itemId === item.id,
                                            ),
                                        )
                                        .map((item) => ({
                                          value: item.id,
                                          label: `${item.sku ? `${item.sku} · ` : ""}${item.name}`,
                                        }))}
                                      optionLabel="label"
                                      optionValue="value"
                                    >
                                      <Select.Trigger type="button">
                                        <Select.Value />
                                        <Select.Indicator className="pi pi-chevron-down" />
                                      </Select.Trigger>
                                      <Select.Portal>
                                        <Select.Positioner>
                                          <Select.Popup>
                                            <Select.List />
                                          </Select.Popup>
                                        </Select.Positioner>
                                      </Select.Portal>
                                    </Select.Root>
                                  </DataTable.Cell>
                                  <DataTable.Cell>
                                    <InputNumber.Root
                                      fluid
                                      min={0.001}
                                      maxFractionDigits={3}
                                      value={Number(component.quantity)}
                                      onValueChange={(event: {
                                        value: number | null;
                                      }) =>
                                        setDraft({
                                          ...draft,
                                          packageItems: draft.packageItems.map(
                                            (row, index) =>
                                              index === component.index
                                                ? {
                                                    ...row,
                                                    quantity:
                                                      event.value == null
                                                        ? ""
                                                        : String(event.value),
                                                  }
                                                : row,
                                          ),
                                        })
                                      }
                                    >
                                      <InputNumber.Input
                                        as={Input}
                                        aria-label={t("commercial.quantity", {
                                          defaultValue: "Quantity",
                                        })}
                                        required
                                      />
                                    </InputNumber.Root>
                                  </DataTable.Cell>
                                  <DataTable.Cell>
                                    <Button
                                      type="button"
                                      icon="pi pi-trash"
                                      variant="text"
                                      severity="danger"
                                      aria-label={t("common.remove")}
                                      onClick={() =>
                                        setDraft({
                                          ...draft,
                                          packageItems:
                                            draft.packageItems.filter(
                                              (_, index) =>
                                                index !== component.index,
                                            ),
                                        })
                                      }
                                    />
                                  </DataTable.Cell>
                                </DataTable.Row>
                              );
                            }}
                          </DataTable.TBody>
                          <DataTable.EmptyTBody>
                            <DataTable.Row>
                              <DataTable.Cell
                                colSpan={3}
                                className="py-6 text-center text-app-muted"
                              >
                                {t("catalog.noPackageItems", {
                                  defaultValue: "No items in this package.",
                                })}
                              </DataTable.Cell>
                            </DataTable.Row>
                          </DataTable.EmptyTBody>
                        </DataTable.Table>
                      </DataTable.TableContainer>
                    </DataTable.Root>
                  </Section>
                ) : null}
                <div className="space-y-3 rounded-lg border border-app-border p-3">
                  {draft.kind === "package" ? (
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox.Root
                        checked={draft.stockEnabled}
                        onCheckedChange={(event: { checked: boolean }) =>
                          setDraft({
                            ...draft,
                            stockEnabled: Boolean(event.checked),
                          })
                        }
                      >
                        <Checkbox.Box>
                          <Checkbox.Indicator match="checked">
                            <i className="pi pi-check" />
                          </Checkbox.Indicator>
                        </Checkbox.Box>
                      </Checkbox.Root>
                      {t("catalog.stockEnabled", {
                        defaultValue: "Track package stock independently",
                      })}
                    </label>
                  ) : (
                    <p className="text-sm text-app-muted">
                      {t("catalog.itemsAlwaysTracked", {
                        defaultValue: "Items are always inventory-enabled.",
                      })}
                    </p>
                  )}
                  {draft.kind === "item" || draft.stockEnabled ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <Field
                        label={t("catalog.defaultLocation", {
                          defaultValue: "Default location",
                        })}
                      >
                        <Select.Root
                          fluid
                          value={draft.defaultLocationId || null}
                          onValueChange={(event: { value: unknown }) =>
                            setDraft({
                              ...draft,
                              defaultLocationId: String(event.value ?? ""),
                            })
                          }
                          options={[
                            {
                              value: "",
                              label: t("common.none", {
                                defaultValue: "None",
                              }),
                            },
                            ...(locations ?? [])
                              .filter((location) => location.active)
                              .map((location) => ({
                                value: location.id,
                                label: `${location.code} · ${location.name}`,
                              })),
                          ]}
                          optionLabel="label"
                          optionValue="value"
                        >
                          <Select.Trigger type="button">
                            <Select.Value />
                            <Select.Indicator className="pi pi-chevron-down" />
                          </Select.Trigger>
                          <Select.Portal>
                            <Select.Positioner>
                              <Select.Popup>
                                <Select.List />
                              </Select.Popup>
                            </Select.Positioner>
                          </Select.Portal>
                        </Select.Root>
                      </Field>
                      <Field
                        label={t("catalog.defaultUnitCost", {
                          defaultValue: "Default unit cost",
                        })}
                      >
                        <InputNumber.Root
                          fluid
                          min={0}
                          maxFractionDigits={2}
                          value={
                            draft.defaultUnitCost
                              ? Number(draft.defaultUnitCost)
                              : null
                          }
                          onValueChange={(event: { value: number | null }) =>
                            setDraft({
                              ...draft,
                              defaultUnitCost:
                                event.value == null ? "" : String(event.value),
                            })
                          }
                        >
                          <InputNumber.Input as={Input} />
                        </InputNumber.Root>
                      </Field>
                      <Field
                        label={t("catalog.reorderPoint", {
                          defaultValue: "Reorder point",
                        })}
                      >
                        <InputNumber.Root
                          fluid
                          min={0}
                          maxFractionDigits={3}
                          value={
                            draft.reorderPoint
                              ? Number(draft.reorderPoint)
                              : null
                          }
                          onValueChange={(event: { value: number | null }) =>
                            setDraft({
                              ...draft,
                              reorderPoint:
                                event.value == null ? "" : String(event.value),
                            })
                          }
                        >
                          <InputNumber.Input as={Input} />
                        </InputNumber.Root>
                      </Field>
                    </div>
                  ) : null}
                </div>
                <Field
                  label={t("commercial.description", {
                    defaultValue: "Description",
                  })}
                >
                  <Textarea
                    value={draft.description}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setDraft({ ...draft, description: event.target.value })
                    }
                    rows={4}
                  />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox.Root
                    checked={draft.active}
                    onCheckedChange={(event: { checked: boolean }) =>
                      setDraft({ ...draft, active: Boolean(event.checked) })
                    }
                  >
                    <Checkbox.Box>
                      <Checkbox.Indicator match="checked">
                        <i className="pi pi-check" />
                      </Checkbox.Indicator>
                    </Checkbox.Box>
                  </Checkbox.Root>
                  {t("common.active", { defaultValue: "Active" })}
                </label>
                <MutationError error={saveError} />
              </Dialog.Content>
              <Dialog.Footer>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outlined"
                    severity="secondary"
                    onClick={() => setEditorOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" loading={saving || undefined}>
                    {t("common.save")}
                  </Button>
                </div>
              </Dialog.Footer>
            </form>
          </Dialog.Popup>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
